import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useIntl } from '../../../util/reactIntl';
import { onTriggerError } from '../../../util/toast';

export const AUTOSAVE_DEBOUNCE_MS = 1800;

export const AUTOSAVE_STATUS = {
  SAVED: 'saved',
  UNSAVED: 'unsaved',
  SAVING: 'saving',
  ERROR: 'error',
};

const normalizedValue = (value, seen) => {
  if (value == null || typeof value === 'string' || typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return Number.isNaN(value) ? 'NaN' : value;
  }
  if (typeof value === 'undefined' || typeof value === 'function') {
    return undefined;
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (typeof File !== 'undefined' && value instanceof File) {
    return { name: value.name, size: value.size, type: value.type };
  }
  if (typeof Blob !== 'undefined' && value instanceof Blob) {
    return { size: value.size, type: value.type };
  }
  if (value?._isAMomentObject && typeof value.toISOString === 'function') {
    return value.toISOString();
  }
  if (Array.isArray(value)) {
    return value.map(item => normalizedValue(item, seen));
  }
  if (typeof value === 'object') {
    if (seen.has(value)) {
      return '[Circular]';
    }
    seen.add(value);
    const normalizedObject = Object.keys(value)
      .sort()
      .reduce((result, key) => {
        const normalized = normalizedValue(value[key], seen);
        return typeof normalized === 'undefined' ? result : { ...result, [key]: normalized };
      }, {});
    seen.delete(value);
    return normalizedObject;
  }
  return String(value);
};

export const createAutosaveSnapshot = values =>
  JSON.stringify(normalizedValue(values || {}, new WeakSet()));

export const useListingAutosave = options => {
  const {
    values,
    dirty,
    valid,
    onSave,
    enabled = true,
    blocked = false,
    debounceMs = AUTOSAVE_DEBOUNCE_MS,
  } = options;
  const intl = useIntl();
  const history = useHistory();
  const snapshot = createAutosaveSnapshot(values);
  const [status, setStatusState] = useState(AUTOSAVE_STATUS.SAVED);

  const mountedRef = useRef(true);
  const timerRef = useRef(null);
  const latestRef = useRef({ values, snapshot, dirty, valid, blocked, enabled });
  const lastSavedSnapshotRef = useRef(snapshot);
  const inFlightSnapshotRef = useRef(null);
  const queuedRef = useRef(false);
  const statusRef = useRef(AUTOSAVE_STATUS.SAVED);
  const onSaveRef = useRef(onSave);
  const allowNavigationRef = useRef(false);
  const executeSaveRef = useRef(null);

  const setStatus = useCallback(nextStatus => {
    statusRef.current = nextStatus;
    if (mountedRef.current) {
      setStatusState(nextStatus);
    }
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const scheduleSave = useCallback(
    (delay = debounceMs) => {
      clearTimer();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        executeSaveRef.current?.().catch(() => {});
      }, delay);
    },
    [clearTimer, debounceMs]
  );

  const executeSave = useCallback(() => {
    const latest = latestRef.current;
    if (!enabled || !latest.valid || latest.blocked) {
      return Promise.resolve(null);
    }
    if (latest.snapshot === lastSavedSnapshotRef.current) {
      queuedRef.current = false;
      setStatus(AUTOSAVE_STATUS.SAVED);
      return Promise.resolve(null);
    }
    if (inFlightSnapshotRef.current) {
      queuedRef.current = true;
      return Promise.resolve(null);
    }

    const savingSnapshot = latest.snapshot;
    const savingValues = latest.values;
    inFlightSnapshotRef.current = savingSnapshot;
    queuedRef.current = false;
    setStatus(AUTOSAVE_STATUS.SAVING);

    return Promise.resolve()
      .then(() => onSaveRef.current(savingValues))
      .then(result => {
        lastSavedSnapshotRef.current = savingSnapshot;
        inFlightSnapshotRef.current = null;

        const newest = latestRef.current;
        const hasQueuedChanges =
          queuedRef.current || (newest.dirty && newest.snapshot !== lastSavedSnapshotRef.current);
        if (hasQueuedChanges) {
          queuedRef.current = false;
          setStatus(AUTOSAVE_STATUS.UNSAVED);
          if (newest.enabled) {
            scheduleSave();
          }
        } else {
          setStatus(AUTOSAVE_STATUS.SAVED);
        }
        return result;
      })
      .catch(error => {
        inFlightSnapshotRef.current = null;
        queuedRef.current = false;
        setStatus(AUTOSAVE_STATUS.ERROR);
        if (mountedRef.current) {
          onTriggerError(intl.formatMessage({ id: 'EditListingAutosave.saveFailedToast' }));
        }
        throw error;
      });
  }, [enabled, intl, scheduleSave, setStatus]);

  executeSaveRef.current = executeSave;
  onSaveRef.current = onSave;
  latestRef.current = { values, snapshot, dirty, valid, blocked, enabled };

  useEffect(() => {
    clearTimer();

    if (!dirty) {
      if (!inFlightSnapshotRef.current) {
        lastSavedSnapshotRef.current = snapshot;
        setStatus(AUTOSAVE_STATUS.SAVED);
      }
      return undefined;
    }

    if (snapshot === lastSavedSnapshotRef.current) {
      setStatus(AUTOSAVE_STATUS.SAVED);
      return undefined;
    }

    if (inFlightSnapshotRef.current) {
      if (snapshot !== inFlightSnapshotRef.current) {
        queuedRef.current = true;
      }
      return undefined;
    }

    setStatus(AUTOSAVE_STATUS.UNSAVED);
    if (enabled && valid && !blocked) {
      scheduleSave();
    }
    return clearTimer;
  }, [blocked, clearTimer, dirty, enabled, scheduleSave, setStatus, snapshot, valid]);

  const retry = useCallback(() => {
    if (!inFlightSnapshotRef.current && latestRef.current.valid && !latestRef.current.blocked) {
      clearTimer();
      executeSaveRef.current?.().catch(() => {});
    }
  }, [clearTimer]);

  const handleManualSubmit = useCallback(
    handleSubmit => event => {
      clearTimer();

      if (inFlightSnapshotRef.current) {
        if (latestRef.current.snapshot !== inFlightSnapshotRef.current) {
          queuedRef.current = true;
        }
        return Promise.resolve(null);
      }

      queuedRef.current = false;

      if (!latestRef.current.valid) {
        return handleSubmit(event);
      }

      const savingSnapshot = latestRef.current.snapshot;
      inFlightSnapshotRef.current = savingSnapshot;
      allowNavigationRef.current = true;
      setStatus(AUTOSAVE_STATUS.SAVING);

      let result;
      try {
        result = handleSubmit(event);
      } catch (error) {
        inFlightSnapshotRef.current = null;
        allowNavigationRef.current = false;
        setStatus(AUTOSAVE_STATUS.ERROR);
        throw error;
      }

      if (!result || typeof result.then !== 'function') {
        inFlightSnapshotRef.current = null;
        allowNavigationRef.current = false;
        setStatus(latestRef.current.dirty ? AUTOSAVE_STATUS.UNSAVED : AUTOSAVE_STATUS.SAVED);
        return result;
      }

      return Promise.resolve(result)
        .then(response => {
          lastSavedSnapshotRef.current = savingSnapshot;
          inFlightSnapshotRef.current = null;
          const newest = latestRef.current;
          const hasQueuedChanges =
            queuedRef.current || (newest.dirty && newest.snapshot !== lastSavedSnapshotRef.current);
          if (hasQueuedChanges) {
            queuedRef.current = false;
            setStatus(AUTOSAVE_STATUS.UNSAVED);
            if (newest.enabled) {
              scheduleSave();
            }
          } else {
            setStatus(AUTOSAVE_STATUS.SAVED);
          }
          return response;
        })
        .catch(() => {
          inFlightSnapshotRef.current = null;
          queuedRef.current = false;
          setStatus(AUTOSAVE_STATUS.ERROR);
          return undefined;
        })
        .finally(() => {
          allowNavigationRef.current = false;
        });
    },
    [clearTimer, scheduleSave, setStatus]
  );

  useEffect(() => {
    const hasPendingChanges = () =>
      statusRef.current === AUTOSAVE_STATUS.UNSAVED ||
      statusRef.current === AUTOSAVE_STATUS.SAVING ||
      statusRef.current === AUTOSAVE_STATUS.ERROR;
    const confirmationMessage = intl.formatMessage({
      id: 'EditListingAutosave.unsavedNavigationWarning',
    });

    const unblock = history.block(() => {
      if (allowNavigationRef.current || !hasPendingChanges()) {
        return undefined;
      }
      if (!inFlightSnapshotRef.current && latestRef.current.valid && !latestRef.current.blocked) {
        executeSaveRef.current?.().catch(() => {});
      }
      return confirmationMessage;
    });

    const handleBeforeUnload = event => {
      if (!hasPendingChanges()) {
        return undefined;
      }
      if (!inFlightSnapshotRef.current && latestRef.current.valid && !latestRef.current.blocked) {
        executeSaveRef.current?.().catch(() => {});
      }
      event.preventDefault();
      event.returnValue = confirmationMessage;
      return confirmationMessage;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      unblock();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [history, intl]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      clearTimer();
      const latest = latestRef.current;
      if (
        statusRef.current === AUTOSAVE_STATUS.UNSAVED &&
        !inFlightSnapshotRef.current &&
        latest.enabled &&
        latest.valid &&
        !latest.blocked
      ) {
        Promise.resolve()
          .then(() => onSaveRef.current(latest.values))
          .catch(() => {});
      }
    };
  }, [clearTimer]);

  return {
    status,
    retry,
    handleManualSubmit,
    isSaving: status === AUTOSAVE_STATUS.SAVING,
  };
};

export default useListingAutosave;
